import { useState, useRef, useEffect } from "react";
import {
  useEditor,
  EditorContent,
  Extension,
  ReactNodeViewRenderer,
  NodeViewWrapper,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import TextAlign from "@tiptap/extension-text-align";
import { Node } from "@tiptap/core";
import ListItem from "@tiptap/extension-list-item";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Undo2,
  Redo2,
  Link2 as LinkIcon,
  Image as ImageIcon,
  Type,
  X,
  ImagePlus,
  UploadCloud,
  SquareMousePointer,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Eye,
  Code,
} from "lucide-react";
import { deleteImage, uploadImages } from "../../../services/uploaderServices";
import toast from "react-hot-toast";

const divComponent = ({ node }) => {
  const { text, color, link } = node.attrs;

  return (
    <NodeViewWrapper as="div" className="editor-div-wrapper">
      <a
        href={link || "#"}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          backgroundColor: color,
          color: getContrastColor(color),
          padding: "0.5rem 1.5rem",
          borderRadius: "4px",
          border: "none",
          cursor: "pointer",
          fontWeight: "500",
          fontSize: "14px",
          lineHeight: "1.5",
          display: "inline-block",
          textAlign: "center",
          textDecoration: "none",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          transition: "all 0.2s ease",
        }}
        className="editor-div"
        data-type="editor-div"
      >
        {text}
      </a>
    </NodeViewWrapper>
  );
};

// Helper function for text contrast
const getContrastColor = (hexColor) => {
  if (!hexColor) return "#FFFFFF";
  const r = parseInt(hexColor.substr(1, 2), 16);
  const g = parseInt(hexColor.substr(3, 2), 16);
  const b = parseInt(hexColor.substr(5, 2), 16);

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? "#000000" : "#FFFFFF";
};

const divExtension = Node.create({
  name: "div",
  inline: true,
  group: "inline",
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      text: { default: "Click me" },
      color: { default: "#4F22D8" },
      link: { default: null },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="editor-div"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "a",
      {
        "data-type": "editor-div",
        class: "editor-div",
        href: HTMLAttributes.link || "#",
        target: "_blank",
        rel: "noopener noreferrer",
        style: `
        background-color: ${HTMLAttributes.color};
        color: ${getContrastColor(HTMLAttributes.color)};
        padding: 0.5rem 1.5rem;
        border-radius: 4px;
        border: none;
        cursor: pointer;
        font-weight: 500;
        font-size: 14px;
        line-height: 1.5;
        display: inline-block;
        text-align: center;
        text-decoration: none;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        transition: all 0.2s ease;
      `,
      },
      HTMLAttributes.text,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(divComponent);
  },
});

// Link Modal Component
const BlogModal = ({
  closeModal,
  showModal,
  setLinkURL,
  linkURL,
  updateLink,
}) => {
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      updateLink();
    }
  };

  return (
    <>
      {showModal && (
        <div className="fixed inset-0 bg-black/[60%] flex items-center justify-center z-50">
          <div className="w-[25%] min-w-[300px] bg-white flex justify-center items-center h-[200px] rounded-lg relative shadow-xl">
            <div
              className="absolute cursor-pointer top-5 right-5 text-gray-600 hover:text-black transition"
              onClick={closeModal}
            >
              <X size={24} />
            </div>
            <div className="flex flex-col items-start w-[75%] mx-auto">
              <label className="font-semibold mt-8 text-gray-700 text-sm">
                Link URL
              </label>
              <input
                onChange={(e) => setLinkURL(e.target.value)}
                onKeyDown={handleKeyDown}
                value={linkURL}
                type="text"
                placeholder="https://example.com"
                className="appearance-none block w-full border border-gray-300 rounded-md py-2 px-4 mt-2 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                autoFocus
              />
              <button
                onClick={updateLink}
                className="w-full mt-3 flex justify-center items-center rounded-md h-[40px] text-white font-medium text-sm bg-blue-600 hover:bg-blue-700 transition"
              >
                Set Link
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Image Modal Component
const BlogImagePopup = ({
  closeModal,
  showModal,
  inputRef,
  handleFileChange,
  handleInputClick,
  selectedFiles,
  handleDelete,
  handleUpload,
  uploading,
}) => {
  return (
    <>
      {showModal && (
        <div className="fixed inset-0 bg-black/[60%] flex items-center justify-center z-50">
          <div className="w-[25%] min-w-[350px] bg-white p-6 h-[410px] rounded-lg relative shadow-xl overflow-y-auto">
            <div
              className="absolute top-5 right-5 text-gray-600 hover:text-black transition"
              onClick={closeModal}
            >
              <X size={24} />
            </div>
            <div className="w-full">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Upload Image
              </label>
              <input
                ref={inputRef}
                className="hidden"
                onChange={handleFileChange}
                type="file"
                accept="image/*"
                multiple
              />
              <div className="flex justify-between items-center w-full">
                <div
                  onClick={handleInputClick}
                  className="bg-gray-100 hover:bg-gray-200 flex items-center justify-center gap-2 text-gray-800 font-medium py-2 px-4 rounded-md w-full h-[40px] text-sm transition"
                >
                  <ImagePlus size={18} />
                  Choose Files
                </div>
              </div>
            </div>
            <div className="w-full pt-6">
              {selectedFiles?.length > 0 && (
                <>
                  <h1 className="text-sm font-semibold mb-3 text-gray-700">
                    Selected Images
                  </h1>
                  <div className="grid grid-cols-2 gap-3">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="relative group">
                        <img
                          className="w-full h-[100px] rounded-md object-cover border border-gray-200"
                          src={URL.createObjectURL(file)}
                          alt={`image-${index}`}
                        />
                        <div
                          onClick={() => handleDelete(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white w-6 h-6 rounded-full hover:bg-red-600 transition flex items-center justify-center"
                        >
                          <X size={14} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div
                    onClick={handleUpload}
                    disabled={uploading}
                    className={`mt-4 text-white font-medium py-2 px-3 text-sm rounded-md w-full h-[40px] flex items-center justify-center gap-2 ${
                      uploading
                        ? "bg-blue-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    } transition`}
                  >
                    {uploading ? (
                      "Uploading..."
                    ) : (
                      <>
                        <UploadCloud size={18} />
                        Upload Images
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const LinkModal = ({
  closeModal,
  showModal,
  divText,
  setdivText,
  divColor,
  setdivColor,
  divLink,
  setdivLink,
  adddiv,
}) => {
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      adddiv();
    }
  };

  return (
    <>
      {showModal && (
        <div className="fixed inset-0 bg-black/[60%] flex items-center justify-center z-50">
          <div className="w-[25%] min-w-[300px] bg-white flex justify-center items-center h-[320px] rounded-lg relative shadow-xl">
            <div
              className="absolute top-5 right-5 text-gray-600 hover:text-black transition"
              onClick={closeModal}
            >
              <X size={24} />
            </div>
            <div className="flex flex-col items-start w-[75%] mx-auto">
              <label className="font-semibold mt-8 text-gray-700 text-sm mb-1">
                Text
              </label>
              <input
                onChange={(e) => setdivText(e.target.value)}
                onKeyDown={handleKeyDown}
                value={divText}
                type="text"
                placeholder="div text"
                className="appearance-none h-10 block w-full border border-gray-300 rounded-md py-1 px-4 mb-3 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                autoFocus
              />

              <label className="font-semibold text-gray-700 text-sm mb-1">
                Link
              </label>
              <input
                onChange={(e) => setdivLink(e.target.value)}
                value={divLink}
                type="url"
                placeholder="https://example.com"
                className="appearance-none h-10 block w-full border border-gray-300 rounded-md py-1 px-4 mb-4 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
              />

              <div
                onClick={adddiv}
                className="w-full rounded-md h-[40px] text-white font-medium text-sm bg-[#4F22D8] flex justify-center items-center transition"
              >
                Add Link
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export const RichTextEditor = ({
  description = "",
  onChange,
  setFinalPreview,
}) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showdivModal, setShowdivModal] = useState(false);
  const [linkURL, setLinkURL] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [divText, setdivText] = useState("Click here");
  const [divColor, setdivColor] = useState("#4F22D8");
  const [divLink, setdivLink] = useState("");
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: false,
        orderedList: false,
        listItem: false,
      }),
      TextAlign.configure({
        types: ["heading", "paragraph", "image"],
        defaultAlignment: "left",
      }),
      Underline,
      TextStyle,
      Color,
      BulletList.configure({
        itemTypeName: "listItem",
        HTMLAttributes: {
          class: "prose-bullet-list",
        },
      }),
      OrderedList.configure({
        itemTypeName: "listItem",
        HTMLAttributes: {
          class: "prose-ordered-list",
        },
      }),
      ListItem.configure({
        HTMLAttributes: {
          class: "list-item",
        },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          class: "text-blue-600 underline hover:text-blue-800 transition",
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: "editor-image",
        },
      }),
      divExtension,
    ],
    content: description,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange?.(html);
    },
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none min-h-[300px] p-4",
      },
      handleDOMEvents: {
        keydown: (view, event) => {
          if (event.key === "Enter") {
            const { state } = view;
            const { selection } = state;
            const { $from, empty } = selection;

            if (!empty || $from.parentOffset < $from.parent.content.size) {
              return false;
            }

            if ($from.parent.type.name === "listItem") {
              if ($from.parent.content.size === 0) {
                view.dispatch(
                  state.tr
                    .lift($from.blockRange($from))
                    .split($from.pos)
                    .scrollIntoView()
                );
                return true;
              }

              view.dispatch(state.tr.split($from.pos, 2).scrollIntoView());
              return true;
            }
          }
          return false;
        },
      },
    },
  });

  useEffect(() => {
    if (editor && description !== editor.getHTML()) {
      editor.commands.setContent(description);
    }
  }, [description, editor]);

  useEffect(() => {
    if (editor && setFinalPreview) {
      const fullTemplate = generateFullEmailTemplate(description);
      setFinalPreview(fullTemplate);
    }
  }, [description, editor, setFinalPreview]);

  // Link Modal Functions
  const openLinkModal = () => {
    if (editor.isActive("link")) {
      setLinkURL(editor.getAttributes("link").href || "");
    } else {
      setLinkURL("");
    }
    setShowLinkModal(true);
  };

  const closeLinkModal = () => {
    setShowLinkModal(false);
    setLinkURL("");
  };

  const updateLink = () => {
    if (linkURL === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      if (editor.state.selection.empty) {
        editor
          .chain()
          .focus()
          .insertContent({
            type: "text",
            text: linkURL,
            marks: [
              {
                type: "link",
                attrs: {
                  href: linkURL,
                },
              },
            ],
          })
          .run();
      } else {
        editor.chain().focus().setLink({ href: linkURL }).run();
      }
    }
    closeLinkModal();
  };

  // Image Modal Functions
  const openImageModal = () => {
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedFiles([]);
  };

  const handleInputClick = () => {
    inputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles([...selectedFiles, ...files]);
  };

  const handleDelete = async (index) => {
    try {
      const fileToDelete = selectedFiles[index];

      if (typeof fileToDelete === "string") {
        await deleteImage(fileToDelete);
        toast.success("Image deleted successfully");
      }

      const newFiles = selectedFiles.filter((_, i) => i !== index);
      setSelectedFiles(newFiles);

      editor.commands.deleteSelection();
    } catch (err) {
      toast.error(err.message || "Failed to delete image");
    }
  };

  const handleUpload = async () => {
    if (!selectedFiles.length) {
      toast.error("Please select at least one file");
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();

      // Apply white background to all selected files
      const processedFiles = await Promise.all(
        selectedFiles.map((file) => convertToWhiteBg(file))
      );

      processedFiles.forEach((file) => {
        formData.append("images", file);
      });

      const response = await uploadImages(formData);

      if (response?.success && response?.images?.length) {
        toast.success("Images uploaded successfully!");

        response.images.forEach((img) => {
          editor
            .chain()
            .focus()
            .setImage({ src: img.imageUrl })
            .setTextAlign("center")
            .run();
        });

        setSelectedFiles([]);
        closeImageModal();
      } else {
        throw new Error("Image upload failed");
      }
    } catch (err) {
      toast.error(err?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const convertToWhiteBg = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      const img = new window.Image();
      reader.onload = () => {
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;

          const ctx = canvas.getContext("2d");
          if (!ctx) return resolve(file);

          ctx.fillStyle = "white";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);

          canvas.toBlob((blob) => {
            if (blob) {
              const newFile = new File([blob], file.name, {
                type: "image/jpeg",
              });
              resolve(newFile);
            } else {
              resolve(file);
            }
          }, "image/jpeg");
        };

        img.src = reader.result;
      };

      reader.readAsDataURL(file);
    });
  };

  // div Modal Functions
  const opendivModal = () => {
    setShowdivModal(true);
  };

  const closedivModal = () => {
    setShowdivModal(false);
  };

  const adddivToEditor = () => {
    editor
      .chain()
      .focus()
      .insertContent({
        type: "div",
        attrs: {
          text: divText,
          color: divColor,
          link: divLink,
        },
      })
      .setTextAlign("center")
      .run();
    closedivModal();
    setdivText("Click here");
    setdivColor("#4F22D8");
    setdivLink("");
  };

  const colors = [
    "#000000",
    "#e60000",
    "#ff9900",
    "#ffff00",
    "#008a00",
    "#0066cc",
    "#9933ff",
    "#ffffff",
    "#facccc",
    "#ffebcc",
    "#ffffcc",
    "#cce8cc",
    "#cce0f5",
    "#ebd6ff",
    "#bbbbbb",
    "#f06666",
    "#ffc266",
    "#ffff66",
    "#66b266",
    "#66a3e0",
    "#c285ff",
    "#888888",
    "#a10000",
    "#b26b00",
    "#b2b200",
    "#006100",
    "#0047b2",
    "#6b24b2",
    "#444444",
    "#5c0000",
    "#663d00",
    "#666600",
    "#003700",
    "#002966",
    "#3d1466",
  ];

  const generateFullEmailTemplate = (content) => {
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Email Template</title>
  <style>
    /* Scoped Styles for Injected Content */
    .email-content h1, 
    .email-content h2, 
    .email-content h3, 
    .email-content h4 {
      margin: 20px auto 10px;
      max-width: 90%;
      text-align: center;
      font-weight: bold;
      color: #333;
    }

    .email-content h1 { font-size: 24px; }
    .email-content h2 { font-size: 22px; }
    .email-content h3 { font-size: 20px; }
    .email-content h4 { font-size: 18px; }

    .email-content p {
      max-width: 70%;
      margin: 0 auto 20px;
      font-size: 16px;
      line-height: 1.6;
      color: #444;
      text-align: center;
    }

    .email-content ul, 
    .email-content ol {
      text-align: left;
      margin: 0 auto 20px;
      padding-left: 20px;
      max-width: 500px;
      color: #444;
      font-size: 16px;
    }

    .email-content li {
      margin-bottom: 10px;
    }

    .email-content a {
      color: #5525EB;
      text-decoration: none;
      font-weight: bold;
    }

    .email-content a:hover {
      text-decoration: underline;
    }

    /* Fixed: Images scale to 90% of container width */
    .email-content img {
      display: block;
      width: 90%;
      height: auto;
      margin: 20px auto;
      border-radius: 6px;
      border: 0;
    }

    .email-content button {
      background-color: #5525EB;
      color: #fff;
      padding: 12px 20px;
      border: none;
      border-radius: 5px;
      font-size: 16px;
      cursor: pointer;
      margin: 20px auto;
      display: inline-block;
    }

    .email-content div {
      margin: 20px auto;
      text-align: center;
    }
  </style>
</head>
<body style="font-family: Arial, sans-serif; margin:0; padding:0; background:#f9f9f9;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#f9f9f9; margin:0; padding:0;">
    <tr>
      <td align="center" valign="top" style="padding:30px 10px;">
        <table width="600" cellpadding="0" cellspacing="0" border="0" style="max-width:600px; background:#ffffff; border:1px solid #ddd; border-radius:8px; box-shadow:0 4px 12px rgba(0,0,0,0.05);">
          <tr>
            <td style="padding:30px 20px; text-align:center;">
              <!-- Logo -->
              <img src="https://bookmyretreat-v1.s3.ap-south-1.amazonaws.com/f736dda8-f6f6-492d-bb82-09684ad138ea-1752649389095.webp" 
                   alt="Logo" 
                   width="300"
                   style="display:block; margin:0 auto 30px; max-width:300px; height:auto; border:0;">

              <!-- Main Content Wrapped for Scoped Styling -->
              <div class="email-content">
                ${content}
              </div>

              <!-- Footer -->
              <div style="margin-top:40px; padding-top:20px; border-top:1px solid #eee; text-align:center;">
                <p style="color:#999; font-size:12px; margin:0;">
                  If you have questions, just reply to this email.
                </p>
              </div>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
  };

  const handlePreview = () => {
    const fullTemplate = generateFullEmailTemplate(description);
    setShowPreview(!showPreview);
    if (!showPreview) {
      const previewWindow = window.open("", "_blank");
      previewWindow.document.write(fullTemplate);
      previewWindow.document.close();
    }
  };

  if (!editor) return null;

  return (
    <div className="flex flex-col space-y-2">
      <div className="flex justify-between items-center">
        <span className="text-gray-700 font-medium text-sm">Description</span>
        <div
          onClick={handlePreview}
          className="flex items-center gap-2 px-3 py-1 bg-[#4F22D8] text-white rounded-md transition text-sm"
        >
          <Eye size={14} />
          Preview Email
        </div>
      </div>

      <div className="w-full bg-white border border-gray-300 rounded-lg overflow-hidden">
        <div className="bg-gray-50 border-b border-gray-200 p-3">
          <div className="flex flex-wrap gap-2 items-center">
            {/* Undo/Redo */}
            <div className="flex gap-1 border-r border-gray-300 pr-2 mr-2">
              <div
                onClick={() => editor.chain().focus().undo().run()}
                disabled={!editor.can().undo()}
                className="p-2 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition"
                title="Undo"
              >
                <Undo2 size={16} />
              </div>
              <div
                onClick={() => editor.chain().focus().redo().run()}
                disabled={!editor.can().redo()}
                className="p-2 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition"
                title="Redo"
              >
                <Redo2 size={16} />
              </div>
            </div>

            {/* Text Style */}
            <div className="flex items-center gap-1 border-r border-gray-300 pr-2 mr-2">
              <select
                onChange={(e) => {
                  const level = parseInt(e.target.value);
                  if (level === 0) {
                    editor.chain().focus().setParagraph().run();
                  } else {
                    editor.chain().focus().toggleHeading({ level }).run();
                  }
                }}
                className="text-xs border border-gray-300 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                value={
                  editor.isActive("heading", { level: 1 })
                    ? "1"
                    : editor.isActive("heading", { level: 2 })
                    ? "2"
                    : editor.isActive("heading", { level: 3 })
                    ? "3"
                    : "0"
                }
              >
                <option value="0">Normal</option>
                <option value="1">Heading 1</option>
                <option value="2">Heading 2</option>
                <option value="3">Heading 3</option>
              </select>
            </div>

            {/* Text Alignment */}
            <div className="flex gap-1 border-r border-gray-300 pr-2 mr-2">
              <div
                onClick={() =>
                  editor.chain().focus().setTextAlign("left").run()
                }
                className={`p-2 rounded-md hover:bg-gray-200 ${
                  editor.isActive({ textAlign: "left" })
                    ? "bg-blue-100 text-blue-600"
                    : ""
                } transition`}
                title="Align Left"
              >
                <AlignLeft size={16} />
              </div>
              <div
                onClick={() =>
                  editor.chain().focus().setTextAlign("center").run()
                }
                className={`p-2 rounded-md hover:bg-gray-200 ${
                  editor.isActive({ textAlign: "center" })
                    ? "bg-blue-100 text-blue-600"
                    : ""
                } transition`}
                title="Align Center"
              >
                <AlignCenter size={16} />
              </div>
              <div
                onClick={() =>
                  editor.chain().focus().setTextAlign("right").run()
                }
                className={`p-2 rounded-md hover:bg-gray-200 ${
                  editor.isActive({ textAlign: "right" })
                    ? "bg-blue-100 text-blue-600"
                    : ""
                } transition`}
                title="Align Right"
              >
                <AlignRight size={16} />
              </div>
            </div>

            {/* Basic Formatting */}
            <div className="flex gap-1 border-r border-gray-300 pr-2 mr-2">
              <div
                onClick={() => editor.chain().focus().toggleBold().run()}
                className={`p-2 rounded-md hover:bg-gray-200 ${
                  editor.isActive("bold") ? "bg-blue-100 text-blue-600" : ""
                } transition`}
                title="Bold"
              >
                <Bold size={16} />
              </div>
              <div
                onClick={() => editor.chain().focus().toggleItalic().run()}
                className={`p-2 rounded-md hover:bg-gray-200 ${
                  editor.isActive("italic") ? "bg-blue-100 text-blue-600" : ""
                } transition`}
                title="Italic"
              >
                <Italic size={16} />
              </div>
              <div
                onClick={() => editor.chain().focus().toggleUnderline().run()}
                className={`p-2 rounded-md hover:bg-gray-200 ${
                  editor.isActive("underline")
                    ? "bg-blue-100 text-blue-600"
                    : ""
                } transition`}
                title="Underline"
              >
                <UnderlineIcon size={16} />
              </div>
            </div>

            {/* Lists */}
            <div className="flex gap-1 border-r border-gray-300 pr-2 mr-2">
              <div
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                className={`p-2 rounded-md hover:bg-gray-200 ${
                  editor.isActive("bulletList")
                    ? "bg-blue-100 text-blue-600"
                    : ""
                } transition`}
                title="Bullet List"
              >
                <List size={16} />
              </div>
              <div
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                className={`p-2 rounded-md hover:bg-gray-200 ${
                  editor.isActive("orderedList")
                    ? "bg-blue-100 text-blue-600"
                    : ""
                } transition`}
                title="Numbered List"
              >
                <ListOrdered size={16} />
              </div>
            </div>

            {/* Color Picker */}
            <div className="flex gap-1 border-r border-gray-300 pr-2 mr-2 relative">
              <div
                onClick={() => setShowColorPicker(!showColorPicker)}
                className="p-2 rounded-md hover:bg-gray-200 flex items-center gap-1 transition"
                title="Text Color"
              >
                <Type size={16} />
                <div
                  className="w-4 h-1 rounded"
                  style={{
                    backgroundColor:
                      editor.getAttributes("textStyle").color || "#000000",
                  }}
                />
              </div>

              {showColorPicker && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-2 z-10">
                  <div className="grid grid-cols-7 gap-1 w-48">
                    {colors.map((color) => (
                      <div
                        key={color}
                        onClick={() => {
                          editor.chain().focus().setColor(color).run();
                          setShowColorPicker(false);
                        }}
                        className="w-6 h-6 rounded border border-gray-300 hover:scale-110 transition-transform"
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                  <div
                    onClick={() => {
                      editor.chain().focus().unsetColor().run();
                      setShowColorPicker(false);
                    }}
                    className="mt-2 w-full text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded transition"
                  >
                    Remove Color
                  </div>
                </div>
              )}
            </div>

            {/* Link & Media */}
            <div className="flex gap-1">
              <div
                onClick={openLinkModal}
                className={`p-2 rounded-md hover:bg-gray-200 ${
                  editor.isActive("link") ? "bg-blue-100 text-blue-600" : ""
                } transition`}
                title="Add Link"
              >
                <LinkIcon size={16} />
              </div>
              <div
                onClick={openImageModal}
                className="p-2 rounded-md hover:bg-gray-200 transition"
                title="Add Image"
              >
                <ImageIcon size={16} />
              </div>
              <div
                onClick={opendivModal}
                className="p-2 rounded-md hover:bg-gray-200 transition"
                title="Add div"
              >
                <SquareMousePointer size={16} />
              </div>
            </div>
          </div>
        </div>

        {/* Editor Content */}
        <div className="p-0">
          <EditorContent
            editor={editor}
            className="min-h-[300px] focus-within:outline-none"
          />
        </div>
      </div>

      {/* Modals */}
      <BlogModal
        closeModal={closeLinkModal}
        showModal={showLinkModal}
        setLinkURL={setLinkURL}
        linkURL={linkURL}
        updateLink={updateLink}
      />

      <BlogImagePopup
        closeModal={closeImageModal}
        showModal={showImageModal}
        inputRef={inputRef}
        handleFileChange={handleFileChange}
        handleInputClick={handleInputClick}
        selectedFiles={selectedFiles}
        handleDelete={handleDelete}
        handleUpload={handleUpload}
        uploading={uploading}
      />

      <LinkModal
        closeModal={closedivModal}
        showModal={showdivModal}
        divText={divText}
        setdivText={setdivText}
        divColor={divColor}
        setdivColor={setdivColor}
        divLink={divLink}
        setdivLink={setdivLink}
        adddiv={adddivToEditor}
      />

      {/* <div className="mt-4 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <Code size={16} />
          <span className="text-sm font-medium">Generated Email HTML:</span>
        </div>
        <textarea
          value={generateFullEmailTemplate(description)}
          readOnly
          className="w-full h-32 p-2 text-xs font-mono bg-white border border-gray-300 rounded-md resize-none"
          placeholder="Email-compatible HTML will appear here..."
        />
      </div> */}

      <style jsx global>{`
        .ProseMirror {
          outline: none;
          min-height: 300px;
          padding: 16px;
        }

        .ProseMirror-focused {
          outline: none;
        }

        .ProseMirror ul.prose-bullet-list {
          list-style-type: disc;
          margin-left: 1.5rem;
          padding-left: 0;
        }

        .ProseMirror ol.prose-ordered-list {
          list-style-type: decimal;
          margin-left: 1.5rem;
          padding-left: 0;
        }

        .ProseMirror ul.prose-bullet-list li,
        .ProseMirror ol.prose-ordered-list li {
          margin: 0.25rem 0;
        }

        .ProseMirror h1 {
          font-size: 2rem;
          font-weight: 700;
          margin: 1rem 0;
          line-height: 1.3;
          color: #111827;
        }

        .ProseMirror h2 {
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0.875rem 0;
          line-height: 1.3;
          color: #111827;
        }

        .ProseMirror h3 {
          font-size: 1.25rem;
          font-weight: 700;
          margin: 0.75rem 0;
          line-height: 1.3;
          color: #111827;
        }

        .ProseMirror p {
          margin: 0.75rem 0;
          line-height: 1.6;
          color: #374151;
        }

        .ProseMirror a {
          color: #2563eb;
          text-decoration: underline;
          transition: color 0.2s ease;
        }

        .ProseMirror a:hover {
          color: #1d4ed8;
        }

        .ProseMirror code {
          background-color: #f3f4f6;
          padding: 0.2rem 0.4rem;
          border-radius: 0.25rem;
          font-family: "Courier New", monospace;
          font-size: 0.9em;
          color: #dc2626;
        }

        .ProseMirror pre {
          background-color: #1f2937;
          color: #f9fafb;
          padding: 1rem;
          border-radius: 0.5rem;
          overflow-x: auto;
          margin: 1rem 0;
          font-family: "Courier New", monospace;
          font-size: 0.9em;
          line-height: 1.5;
        }

        .ProseMirror pre code {
          background: none;
          padding: 0;
          color: inherit;
          font-size: inherit;
        }

        .ProseMirror blockquote {
          border-left: 4px solid #e5e7eb;
          padding-left: 1rem;
          margin: 1rem 0;
          font-style: italic;
          color: #6b7280;
        }

        .ProseMirror hr {
          border: none;
          border-top: 2px solid #e5e7eb;
          margin: 1.5rem 0;
        }

        .ProseMirror table {
          border-collapse: collapse;
          table-layout: fixed;
          width: 100%;
          margin: 1rem 0;
          overflow: hidden;
        }

        .ProseMirror table td,
        .ProseMirror table th {
          min-width: 1rem;
          border: 2px solid #d1d5db;
          padding: 0.5rem;
          vertical-align: top;
          box-sizing: border-box;
          position: relative;
        }

        .ProseMirror table th {
          font-weight: bold;
          text-align: left;
          background-color: #f9fafb;
        }

        .editor-image {
          display: block;
          margin: 1rem auto;
          max-width: 100%;
          height: auto;
          max-height: 400px;
          border-radius: 0.5rem;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .editor-div-wrapper {
          display: flex;
          justify-content: center;
          margin: 1.5rem 0;
          width: 100%;
        }

        .editor-div {
          min-width: 120px;
          height: 40px;
          padding: 0 1.5rem;
          border-radius: 4px;
          border: none;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .editor-div:hover {
          filter: brightness(90%);
          transform: translateY(-1px);
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        /* Text alignment classes */
        .ProseMirror p[data-text-align="center"],
        .ProseMirror h1[data-text-align="center"],
        .ProseMirror h2[data-text-align="center"],
        .ProseMirror h3[data-text-align="center"],
        .ProseMirror div[data-text-align="center"] {
          text-align: center;
        }

        .ProseMirror p[data-text-align="right"],
        .ProseMirror h1[data-text-align="right"],
        .ProseMirror h2[data-text-align="right"],
        .ProseMirror h3[data-text-align="right"],
        .ProseMirror div[data-text-align="right"] {
          text-align: right;
        }

        .ProseMirror p[data-text-align="left"],
        .ProseMirror h1[data-text-align="left"],
        .ProseMirror h2[data-text-align="left"],
        .ProseMirror h3[data-text-align="left"],
        .ProseMirror div[data-text-align="left"] {
          text-align: left;
        }

        /* Image alignment */
        .ProseMirror .editor-image[data-text-align="center"] {
          margin-left: auto;
          margin-right: auto;
        }

        .ProseMirror .editor-image[data-text-align="right"] {
          margin-left: auto;
          margin-right: 0;
        }

        .ProseMirror .editor-image[data-text-align="left"] {
          margin-left: 0;
          margin-right: auto;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
